import type { SyncProvider, Y } from '@tunasong/sync-lib'
import { useEffect, useState } from 'react'
import { UndoManager } from 'yjs'
import { YJS_DEFAULT_LOCAL_ORIGIN } from '../local-origin.js'

export interface YjsSyncState {
  /** Local YDoc → IndexedDB syncing complete */
  localSynced: boolean
  /** Local IndexedDB → server syncing complete (current session) */
  serverSynced: boolean
  /** Number of local-origin updates applied since the last server `synced` ack */
  unsyncedUpdateCount: number
}

export interface UseYjsSyncStateProps {
  doc: Y.Doc
  provider?: SyncProvider | undefined
  /** Whether the local IndexedDB sync has completed. */
  isLocalSynced: boolean
}

const isLocalOrigin = (origin: unknown): boolean =>
  origin === YJS_DEFAULT_LOCAL_ORIGIN || origin instanceof UndoManager

/**
 * Combines the local IndexedDB-sync flag, the websocket provider's
 * `synced` event, and a count of local-origin YDoc updates so the editor
 * chrome can render a sync-status dot ("green / yellow / grey").
 *
 * The hook does NOT subscribe to entity ids — instead it takes the live
 * doc + provider + local-synced flag that the CollabWebsocketEditor
 * already has, because providers aren't registered globally.
 */
export const useYjsSyncState = (props: UseYjsSyncStateProps): YjsSyncState => {
  const { doc, provider, isLocalSynced } = props
  const [serverSynced, setServerSynced] = useState<boolean>(false)
  const [unsyncedUpdateCount, setUnsyncedUpdateCount] = useState(0)

  useEffect(() => {
    if (!provider) {
      setServerSynced(false)
      return
    }
    // SyncProvider doesn't expose a current `isSynced` value — we wait for
    // the provider's `synced` event. On first mount this is false until the
    // first server ack arrives.
    setServerSynced(false)
    const handleSynced = (isSynced: boolean) => {
      setServerSynced(isSynced)
      if (isSynced) {
        setUnsyncedUpdateCount(0)
      }
    }
    provider.on('synced', handleSynced)
    return () => {
      provider.off('synced', handleSynced)
    }
  }, [provider])

  useEffect(() => {
    const handleUpdate = (_update: Uint8Array, origin: unknown) => {
      if (!isLocalOrigin(origin)) {
        return
      }
      setUnsyncedUpdateCount(n => n + 1)
    }
    doc.on('update', handleUpdate)
    return () => {
      doc.off('update', handleUpdate)
    }
  }, [doc])

  return {
    localSynced: isLocalSynced,
    serverSynced,
    unsyncedUpdateCount,
  }
}
